import React from 'react';
import Price from '@ajat/design-system/Price';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../shared';

const TYPES = ['small', 'regular', 'medium', 'package', 'shopping-cart', 'large', 'x-large', 'xx-large', 'editorial'];

const DEMO = {
  small: { price: '159,-', originalPrice: '159,-', discounted: '120,-', badgeText: 'Included in package 0:-' },
  regular: { price: '159,-', originalPrice: '159,-', discounted: '120,-', badgeText: 'SPARA 200:-' },
  medium: { price: '159,-', originalPrice: '159,-', discounted: '159,-', badgeText: 'Included in package 0:-' },
  package: { price: '159,-', originalPrice: '1753,-', discounted: '700,-', badgeText: 'Included in package 0:-', savingsText: 'Spar 60%' },
  'shopping-cart': { price: '640,-', originalPrice: '640,-', discounted: '340,-', badgeText: 'Included' },
  large: { price: '2244,-', originalPrice: '3159,-', discounted: '2244,-' },
  'x-large': { price: '2244,-', originalPrice: '3340,-', discounted: '2244,-', badgeText: 'Included' },
  'xx-large': { price: '2244,-' },
  editorial: { price: '499,-', originalPrice: '699,-', discounted: '499,-' },
};

const STATES = {
  small: ['default', 'discounted', 'included'],
  regular: ['default', 'discounted', 'included'],
  medium: ['default', 'discounted', 'included'],
  package: ['default', 'discounted', 'included'],
  'shopping-cart': ['default', 'discounted', 'included'],
  large: ['default', 'discounted'],
  'x-large': ['default', 'discounted', 'included'],
  'xx-large': ['default'],
  editorial: ['default', 'discounted'],
};

export const meta = {
  component: Price,
  title: 'Price',
  group: 'Commerce',
  importPath: '@ajat/design-system/Price',
  figmaNode: '3799:17093',
  description:
    'Nine typographic sizes across three states. Never format a price inline — a bare <span>{price},-</span> will not pick up changes to the component, and discounted and included states carry their own colour and strikethrough rules.',
  props: [
    ['type', "'small' | 'regular' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'shopping-cart' | 'package' | 'editorial'", "'small'", 'Typographic size; editorial uses var(--font-editorial)'],
    ['state', "'default' | 'discounted' | 'included'", "'default'", 'Default shows one price; discounted and included add a struck-through original'],
    ['price', 'string', "''", 'Current price, already formatted with its suffix'],
    ['originalPrice', 'string', "''", 'Struck-through original, for discounted and included'],
    ['badgeText', 'string', "'Included'", 'Badge text in the included state'],
    ['savingsText', 'string', "''", 'Savings note, e.g. "Spar 60%"'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'The three states',
      code: `import Price from '@ajat/design-system/Price';

<Price type="regular" price="449,-" />

<Price
  type="large"
  state="discounted"
  price="349,-"
  originalPrice="449,-"
/>

<Price
  type="package"
  state="included"
  price="0,-"
  originalPrice="159,-"
  badgeText="Inkluderet"
/>`,
    },
    {
      title: 'Currency per brand',
      code: `// The component does not format — pass the suffix in.
//   CLS-DK  ',-'      CLS-SE / ABC-SE  ':-'      CLS-FI  ' €'
const suffix = brand === 'clsdk' ? ',-' : ':-';

<Price type="shopping-cart" price={\`\${unitPrice * quantity}\${suffix}\`} />`,
    },
  ],
};

export default function PricePage() {
  return (
    <ComponentPage meta={meta}>
      {TYPES.map((type) => {
        const data = DEMO[type];
        const states = STATES[type];
        return (
          <DemoCard key={type} title={type}>
            <DemoRow align="center">
              {states.includes('default') && (
                <Demo caption="DEFAULT">
                  <Price type={type} state="default" price={data.price} />
                </Demo>
              )}
              {states.includes('discounted') && (
                <Demo caption="DISCOUNTED">
                  <Price
                    type={type}
                    state="discounted"
                    price={data.discounted || data.price}
                    originalPrice={data.originalPrice}
                    savingsText={data.savingsText}
                  />
                </Demo>
              )}
              {states.includes('included') && (
                <Demo caption="INCLUDED">
                  <Price
                    type={type}
                    state="included"
                    price={data.discounted || data.price}
                    originalPrice={data.originalPrice}
                    badgeText={data.badgeText}
                  />
                </Demo>
              )}
            </DemoRow>
          </DemoCard>
        );
      })}
    </ComponentPage>
  );
}
